import { useCallback, useMemo } from "react"
import { ModalBody, ModalFooter, useStyletron } from "@mezo-org/mezo-clay"
import { Form, FormikErrors, FormikProps, withFormik } from "formik"

import { RootState, selectTokenDecimals, useDappSelector } from "#/redux-state"
import { FieldAmountInput } from "#/shared/components/forms"
import SummaryList from "#/shared/components/SummaryList"
import { vault } from "#/shared/constants"
import {
  useEstimateDepositIntoVaultGas,
  usePosthogButtonClickedEvent,
  usePreviewShares,
  useTokenUsdPrice,
} from "#/shared/hooks"
import {
  PosthogAnalyticsId,
  VaultDepositFormValue,
  VaultTokens,
} from "#/shared/types"
import {
  bigIntToHumanReadableFormat,
  formatDuration,
  formUtils,
  getCurrencySymbol,
  separateThousandsWithComma,
  vaultUtils,
} from "#/shared/utils"
import { StepperModalButton } from "#/ui/Modal/StepperModal"

type VaultDepositFormBaseProps = {
  tokenBalance: bigint
}

type VaultDepositFormProps = {
  initialValues: VaultDepositFormValue
  onSubmitForm: (values: VaultDepositFormValue) => void
} & VaultDepositFormBaseProps

function VaultDepositFormBase({
  handleSubmit,
  getFieldMeta,
  isValid,
  tokenBalance,
}: FormikProps<VaultDepositFormValue> & VaultDepositFormBaseProps) {
  const [css, theme] = useStyletron()

  const { value: amount } = getFieldMeta<bigint | undefined>("amount")
  const { value: token } = getFieldMeta<VaultTokens>("token")

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, token),
  )

  const { getTokenUsdValue } = useTokenUsdPrice()

  const balanceUsd = getTokenUsdValue(amount ?? 0n, token)

  const vaultAddress = vault.VAULT_DEPOSIT_TOKEN_TO_VAULT_ADDRESS[token]

  const { data: shares, isFetching: isFetchingShares } = usePreviewShares(
    vaultAddress,
    amount,
  )

  const { estimatedTransactionFee, isFetching: isFetchingTransactionGas } =
    useEstimateDepositIntoVaultGas(vaultAddress, vaultAddress, amount)

  const { emitButtonClickedEvent } = usePosthogButtonClickedEvent()

  const handleClick = useCallback(() => {
    emitButtonClickedEvent(PosthogAnalyticsId.VAULT_REVIEW_DEPOSIT)
  }, [emitButtonClickedEvent])

  const summary = useMemo(
    () => [
      {
        label: "You’ll receive",
        endEnhancer: `${bigIntToHumanReadableFormat(shares ?? 0n)} ${
          vault.VAULT_DEPOSIT_TOKEN_TO_SHARE_TOKEN_NAME[token]
        }`,
        isFetchingShares,
      },
      {
        label: "Collateral exposure",
        endEnhancer: "ETH",
      },
      {
        label: "Avg APY",
        endEnhancer: `${vaultUtils.getAvgAPY()}%`,
      },
      {
        label: "Withdrawal cooldown",
        endEnhancer: formatDuration(vault.WITHDRAWAL_COOLDOWN, "h' h"),
      },
      {
        label: "Gas",
        endEnhancer: `${bigIntToHumanReadableFormat(
          estimatedTransactionFee ?? 0n,
        )} ${getCurrencySymbol("mBTC")}`,
        isFetching: isFetchingTransactionGas,
      },
    ],
    [
      shares,
      token,
      isFetchingShares,
      isFetchingTransactionGas,
      estimatedTransactionFee,
    ],
  )

  return (
    <Form
      onSubmit={handleSubmit}
      className={css({
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      })}
    >
      <ModalBody
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          marginBottom: theme.sizing.scale500,
        }}
      >
        <FieldAmountInput
          token={token}
          balanceToken={tokenBalance}
          fieldName="amount"
          label="Amount"
          tokenDecimals={tokenDecimals}
          balanceUsd={amount ? separateThousandsWithComma(balanceUsd) : "0.00"}
          autoFocus
          autoComplete="off"
        />
        <SummaryList list={summary} marginTop={theme.sizing.scale700} />
      </ModalBody>
      <ModalFooter>
        <StepperModalButton
          type="submit"
          onClick={handleClick}
          disabled={!isValid || amount === undefined}
        >
          Review
        </StepperModalButton>
      </ModalFooter>
    </Form>
  )
}

const VaultDepositForm = withFormik<
  VaultDepositFormProps,
  VaultDepositFormValue
>({
  mapPropsToValues: ({ initialValues }) => initialValues,
  enableReinitialize: true,
  handleSubmit: (values, { props }) => props.onSubmitForm(values),
  validate: ({ amount, token }, { tokenBalance }) => {
    const errors: FormikErrors<VaultDepositFormValue> = {}

    errors.amount = formUtils.validateTokenAmount(amount, tokenBalance, token)

    if (errors.amount) return errors

    return {}
  },
  validateOnBlur: false,
})(VaultDepositFormBase)

export default VaultDepositForm
